import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { purchaseReturnsApi } from './api';
import type { CreatePurchaseReturnPayload, DebitNoteListItem, PurchaseReturnListItem, RefundDebitNotePayload } from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function usePurchaseReturns(params: { purchase_order_id?: string; supplier_id?: string }): UseQueryResult<PurchaseReturnListItem[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['purchase-returns', tenantId, 'list', params],
    queryFn: () => purchaseReturnsApi.list(params),
    enabled: Boolean(params.purchase_order_id || params.supplier_id),
    staleTime: 15_000,
  });
}

export function useDebitNotes(params: { supplier_id?: string; vendor_bill_id?: string }): UseQueryResult<DebitNoteListItem[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['purchase-returns', tenantId, 'notes', params],
    queryFn: () => purchaseReturnsApi.listNotes(params),
    enabled: Boolean(params.supplier_id || params.vendor_bill_id),
    staleTime: 15_000,
  });
}

/** A return moves stock and the payable: PO detail, bills and supplier totals all change. */
function useInvalidate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['purchase-returns', tenantId] });
    void qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
    void qc.invalidateQueries({ queryKey: ['vendor-bills', tenantId] });
    void qc.invalidateQueries({ queryKey: ['suppliers', tenantId] });
  };
}

export function useCreatePurchaseReturn() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (payload: CreatePurchaseReturnPayload) => purchaseReturnsApi.create(payload), onSuccess: () => invalidate() });
}

export function usePostPurchaseReturn() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => purchaseReturnsApi.post(id), onSuccess: () => invalidate() });
}

export function useCancelPurchaseReturn() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => purchaseReturnsApi.cancel(id), onSuccess: () => invalidate() });
}

export function useApplyDebitNote() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, vendorBillId, amount }: { id: string; vendorBillId: string; amount?: number }) =>
      purchaseReturnsApi.applyNote(id, vendorBillId, amount),
    onSuccess: () => invalidate(),
  });
}

export function useRefundDebitNote() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RefundDebitNotePayload }) => purchaseReturnsApi.refundNote(id, payload),
    onSuccess: () => invalidate(),
  });
}
